import { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Search, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface Report {
  id: string;
  description: string;
  location: string;
  hazard_category: string;
  risk_rating: string;
  verification_status: string;
  created_at: string;
  truth_score: number | null;
  reporter_name?: string;
  company_name?: string;
}

const SuperAdminReports = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');

  useEffect(() => {
    fetchReports();
  }, [statusFilter, riskFilter]);

  const fetchReports = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('peer_reports')
        .select(`
          id, description, location, hazard_category, risk_rating, 
          verification_status, created_at, truth_score,
          reporter:worker_profiles!reporter_id(display_name, company_id, companies!worker_profiles_company_id_fkey(company_name))
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (statusFilter !== 'all') {
        query = query.eq('verification_status', statusFilter);
      }
      if (riskFilter !== 'all') {
        query = query.eq('risk_rating', riskFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      const reportsWithMeta = (data || []).map((r: any) => ({
        id: r.id,
        description: r.description,
        location: r.location,
        hazard_category: r.hazard_category,
        risk_rating: r.risk_rating,
        verification_status: r.verification_status,
        created_at: r.created_at,
        truth_score: r.truth_score,
        reporter_name: r.reporter?.display_name || 'Unknown',
        company_name: r.reporter?.companies?.company_name || 'Unknown',
      }));

      setReports(reportsWithMeta);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredReports = reports.filter(report =>
    report.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    report.location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    report.company_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-success">Verified</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      case 'pending_verification':
        return <Badge variant="secondary">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRiskBadge = (risk: string) => {
    switch (risk) {
      case 'high':
      case 'very_high':
        return <Badge variant="destructive">{risk}</Badge>;
      case 'medium':
        return <Badge className="bg-warning text-warning-foreground">{risk}</Badge>;
      default:
        return <Badge variant="secondary">{risk}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">All Platform Reports</h2>
        <p className="text-muted-foreground">View hazard reports from all companies</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search reports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending_verification">Pending</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Select value={riskFilter} onValueChange={setRiskFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by risk" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Risk Levels</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="very_high">Very High</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reports Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Reporter</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Risk</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredReports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No reports found
                </TableCell>
              </TableRow>
            ) : (
              filteredReports.map((report) => (
                <TableRow key={report.id}>
                  <TableCell className="whitespace-nowrap">
                    {format(new Date(report.created_at), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>{report.company_name}</TableCell>
                  <TableCell>{report.reporter_name}</TableCell>
                  <TableCell>{report.hazard_category}</TableCell>
                  <TableCell className="max-w-[150px] truncate">{report.location}</TableCell>
                  <TableCell>{getRiskBadge(report.risk_rating)}</TableCell>
                  <TableCell>{getStatusBadge(report.verification_status)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SuperAdminReports;